import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { ActivityLog, FeedbackStatus } from '../../../utility/feedback';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { ChatService } from '../../../service/chat-service';
import { KeyVaultService } from '../../../service/key-vault.service';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { PlatformRegionService } from '../../../service/shared/platform-region.service';

@Component({
    selector: 'app-feedback-comment-timeline',
    templateUrl: './feedback-comment-timeline.component.html',
    styleUrls: ['./feedback-comment-timeline.component.scss'],
    standalone: false
})
export class FeedbackCommentTimelineComponent implements OnInit {
    uploadedFiles: File[] = [];
    apimUrl: string = '';
    readonly maxFileSizeBytes: number = 5 * 1024 * 1024; // 5MB
    fileSizeError: string = '';

    constructor(
        private toastr: ToastrService,
        private dialog: MatDialog,
        public ChatService: ChatService,
        private readonly keyVaultService: KeyVaultService,
        private platformRegionService: PlatformRegionService
    ) { }

    ngOnInit(): void {
        this.keyVaultService.apimUrl$.pipe(take(1)).subscribe(url => this.apimUrl = url);
    }

    getUploadedFileNames(): string {
        return this.uploadedFiles.map(f => f.name).join(', ');
    }
    @Input() isFileUploadEnabled: boolean = true;
    @Input() feedbackId?: number;
    @Input() requestId?: number;
    @Input() requestType?: string;
    @Input() downloadContext: 'feedback' | 'request' | 'roleplayFeedback' = 'feedback';
    @Output() fileSelected = new EventEmitter<Event>();
    @Input() historyDetails: ActivityLog[] = [];
    @Input() feedbackStatusList: FeedbackStatus[] = [];
    @Input() selectedFeedbackStatus: string = '';
    @Input() commentText: string = '';
    @Input() isSubmitting: boolean = false;
    @Input() isLoading: boolean = false;

    @Output() statusChange = new EventEmitter<string>();
    @Output() submitComment = new EventEmitter<void>();
    @Output() openEvaluationDialog = new EventEmitter<{ tabIndex: number, evaluationOnly?: boolean }>();
    @Output() commentTextChange = new EventEmitter<string>();
    @Output() filesChanged = new EventEmitter<File[]>();

    get statusDisplayLabel(): string {
        return this.selectedFeedbackStatus || '';
    }

    set statusDisplayLabel(value: string) {
        this.selectedFeedbackStatus = value;
    }

    // Status casing differs between APIs (e.g. "IN PROGRESS" vs "In Progress"), so match leniently.
    compareStatus = (a: string, b: string): boolean =>
        (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

    onStatusChange(selectedValue: string): void {
        this.statusChange.emit(selectedValue);
    }

    onSubmit(): void {
        this.submitComment.emit();
    }

    onCommentTextChange(value: string): void {
        this.commentTextChange.emit(value);
        this.commentText = value;
    }

    onOpenEvaluationDialog(tabIndex: number, evaluationOnly = false): void {
        this.openEvaluationDialog.emit({ tabIndex, evaluationOnly });
    }

    onOpenEvaluationOnlyDialog(tabIndex: number): void {
        this.onOpenEvaluationDialog(tabIndex, true);
    }

    isSystemActivity(detail: ActivityLog): boolean {
        return detail.user_type?.toLowerCase() === 'bot' &&
            !detail.assigned_from_category &&
            !detail.assigned_to_category;
    }

    getActivityInitials(detail: ActivityLog): string {
        if (this.isSystemActivity(detail)) return 'SM';
        if (detail.isAI) return 'AI';

        const category = detail.assigned_to_category?.trim();
        if (!category) return 'NA';

        return category
            .split(/\s+/)
            .slice(0, 2)
            .map(part => part[0])
            .join('')
            .toUpperCase();
    }

    getActivityCategory(detail: ActivityLog): string {
        if (this.isSystemActivity(detail)) return 'System';
        return detail.assigned_to_category || 'NA';
    }

    getAssigneeProfile(detail: ActivityLog): string {
        if (this.isSystemActivity(detail)) return 'System';
        return detail.assigned_from_category || detail.assigned_to_category || 'NA';
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.fileSizeError = '';
        if (input.files) {
            const selectedFiles = Array.from(input.files);
            const oversizedFiles = selectedFiles.filter(file => file.size > this.maxFileSizeBytes);
            if (oversizedFiles.length > 0) {
                this.fileSizeError = 'File size should be less than 5MB.';
                input.value = '';
                return;
            }
            this.uploadedFiles = selectedFiles;
            this.filesChanged.emit(this.uploadedFiles);
        }
        this.fileSelected.emit(event);
    }

    clearUploadedFiles(): void {
        this.uploadedFiles = [];
        this.filesChanged.emit([]);
    }

    getAttachmentDisplayName(attachment: string): string {
        if (!attachment) {
            return '';
        }

        const fileName = attachment.split('/').pop() || attachment;
        try {
            return decodeURIComponent(fileName);
        } catch {
            return fileName;
        }
    }

    downloadContactCenterImage(event, imageFileName: string): void {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        if (!imageFileName) {
            this.toastr.error('Image is not available');
            return;
        }
        if (!this.apimUrl) {
            this.toastr.error('Unable to load image. Please try again.');
            return;
        }
        const JWT_token = localStorage.getItem('bearer_token');
        let blobPath: string;
        if (this.downloadContext === 'request') {
            const reqType = this.requestType === 'Bugs' ? 'Bugs' : 'Enhancements';
            blobPath = `${reqType}/${imageFileName}`;
        } else if (this.downloadContext === 'roleplayFeedback') {
            let fileName = imageFileName;
            if (fileName.startsWith('http')) {
                fileName = fileName.split('/').pop() || fileName;
            }
            blobPath = `Roleplay_Feedback/${this.feedbackId}/${fileName}`;
        } else {
            blobPath = `feedbacks/${this.feedbackId}/${imageFileName}`;
        }

        const docUrl = `${this.apimUrl}/ai-advisor-storage/getblobmetadata?blobContainer=aiadvisor-contact-center&fileNameWithExtension=${blobPath}&Authorization=${JWT_token}`;
        const ext = imageFileName.split('.').pop()?.toLowerCase();
        const mimeType = ext === 'pdf' ? 'application/pdf' : ext === 'png' ? 'image/png' : ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : 'application/octet-stream';

        this.ChatService.getPdfReport(docUrl, mimeType).subscribe(res => {
            const fileURL = URL.createObjectURL(res);
            const newTab = window.open(fileURL, '_blank');
            if (!newTab) {
                this.dialog.open(ConfirmationDialogComponent, {
                    width: '400px',
                    data: { message: 'Please ensure your browser is not blocking any popup', okOnly: true },
                    autoFocus: false
                });
            }
        },
            (err) => {
                this.toastr.error('An error occurred while extracting the image... Please try again.', '', { positionClass: 'toast-top-right' });
            });
    }
}